import {
  defaultBevel,
  defaultColorOverlay,
  defaultDropShadow,
  defaultGradientOverlay,
  defaultInnerGlow,
  defaultInnerShadow,
  defaultOuterGlow,
  defaultStroke,
  type LayerEffects,
  type StrokePosition,
} from '../core/layerStyles';
import type { BlendMode, RGBA } from '../core/types';

const BLEND_FROM_ASL: Record<string, BlendMode> = {
  Nrml: 'normal',
  Mul: 'multiply',
  Scrn: 'screen',
  Ovrl: 'overlay',
  DkCl: 'darken',
  LdCl: 'lighten',
  CDdg: 'colorDodge',
  CBrn: 'colorBurn',
  HLgh: 'hardLight',
  SLgh: 'softLight',
  Dfrn: 'difference',
  Xclu: 'exclusion',
  H: 'hue',
  Strt: 'saturation',
  Clr: 'color',
  Lmns: 'luminosity',
};

const STROKE_POS: Record<string, StrokePosition> = {
  OutF: 'outside',
  InsF: 'inside',
  CtrF: 'center',
};

class Reader {
  private o = 0;
  constructor(private readonly view: DataView) {}

  get offset(): number {
    return this.o;
  }

  get remaining(): number {
    return this.view.byteLength - this.o;
  }

  u8(): number {
    const v = this.view.getUint8(this.o);
    this.o += 1;
    return v;
  }

  u16(): number {
    const v = this.view.getUint16(this.o);
    this.o += 2;
    return v;
  }

  u32(): number {
    const v = this.view.getUint32(this.o);
    this.o += 4;
    return v;
  }

  i32(): number {
    const v = this.view.getInt32(this.o);
    this.o += 4;
    return v;
  }

  f64(): number {
    const v = this.view.getFloat64(this.o);
    this.o += 8;
    return v;
  }

  bytes(n: number): Uint8Array {
    const slice = new Uint8Array(this.view.buffer, this.view.byteOffset + this.o, n);
    this.o += n;
    return slice;
  }

  /** Pascal-style signature: 4 bytes, or length-prefixed when length ≠ 4. */
  id(): string {
    const len = this.u32();
    if (len === 0) return this.ascii(4);
    return this.ascii(len);
  }

  ascii(n: number): string {
    const bytes = this.bytes(n);
    let s = '';
    for (let i = 0; i < bytes.length; i += 1) s += String.fromCharCode(bytes[i]!);
    return s;
  }

  unicode(): string {
    const len = this.u32();
    let s = '';
    for (let i = 0; i < len; i += 1) s += String.fromCharCode(this.u16());
    if (len % 2 === 1) this.u16();
    return s.replace(/\0+$/, '');
  }
}

type DescValue =
  | { type: 'bool'; value: boolean }
  | { type: 'double'; value: number }
  | { type: 'unit'; value: number; unit: string }
  | { type: 'enum'; typeId: string; value: string }
  | { type: 'text'; value: string }
  | { type: 'class'; name: string; classId: string }
  | { type: 'object'; classId: string; items: Map<string, DescValue> }
  | { type: 'list'; items: DescValue[] }
  | { type: 'raw'; tag: string };

function readValue(r: Reader): DescValue {
  const tag = r.ascii(4);
  switch (tag) {
    case 'bool':
      return { type: 'bool', value: r.u8() !== 0 };
    case 'doub':
      return { type: 'double', value: r.f64() };
    case 'UntF': {
      const unit = r.ascii(4);
      return { type: 'unit', unit, value: r.f64() };
    }
    case 'enum': {
      const typeId = r.id();
      const value = r.id();
      return { type: 'enum', typeId, value };
    }
    case 'TEXT':
      return { type: 'text', value: r.unicode() };
    case 'type':
    case 'GlbC': {
      const name = r.unicode();
      const classId = r.id();
      return { type: 'class', name, classId };
    }
    case 'Objc':
    case 'GlbO':
      return readObject(r);
    case 'VlLs': {
      const count = r.u32();
      const items: DescValue[] = [];
      for (let i = 0; i < count; i += 1) items.push(readValue(r));
      return { type: 'list', items };
    }
    case 'long':
      return { type: 'double', value: r.i32() };
    case 'comp':
      r.bytes(8);
      return { type: 'raw', tag };
    case 'tdta': {
      const len = r.u32();
      r.bytes(len);
      return { type: 'raw', tag };
    }
    case 'ObAr': {
      const count = r.u32();
      for (let i = 0; i < count; i += 1) readObject(r);
      return { type: 'raw', tag };
    }
    default:
      return { type: 'raw', tag };
  }
}

function readObject(r: Reader): DescValue {
  const name = r.unicode();
  void name;
  const classId = r.id();
  const count = r.u32();
  const items = new Map<string, DescValue>();
  for (let i = 0; i < count; i += 1) {
    const key = r.id();
    items.set(key, readValue(r));
  }
  return { type: 'object', classId, items };
}

function asBool(v: DescValue | undefined, fallback: boolean): boolean {
  return v?.type === 'bool' ? v.value : fallback;
}

function asNumber(v: DescValue | undefined, fallback: number): number {
  if (!v) return fallback;
  if (v.type === 'double' || v.type === 'unit') return v.value;
  return fallback;
}

function asEnum(v: DescValue | undefined): string | null {
  return v?.type === 'enum' ? v.value : null;
}

function asObject(v: DescValue | undefined): Map<string, DescValue> | null {
  return v?.type === 'object' ? v.items : null;
}

function colorFrom(v: DescValue | undefined, fallback: RGBA): RGBA {
  const items = asObject(v);
  if (!items) return fallback;
  // RGB colour descriptor uses Rd/Grn/Bl keys in 0..255.
  const r = asNumber(items.get('Rd'), fallback.r * 255) / 255;
  const g = asNumber(items.get('Grn'), fallback.g * 255) / 255;
  const b = asNumber(items.get('Bl'), fallback.b * 255) / 255;
  return {
    r: Math.min(1, Math.max(0, r)),
    g: Math.min(1, Math.max(0, g)),
    b: Math.min(1, Math.max(0, b)),
    a: 1,
  };
}

function blendFrom(v: DescValue | undefined, fallback: BlendMode): BlendMode {
  const key = asEnum(v);
  if (!key) return fallback;
  return BLEND_FROM_ASL[key] ?? fallback;
}

function opacityFrom(v: DescValue | undefined, fallback: number): number {
  // ASL stores opacity as 0..255 or as a percent unit.
  const n = asNumber(v, fallback * 255);
  return n > 1.5 ? Math.min(1, n / 255) : Math.min(1, Math.max(0, n));
}

function effectFromDescriptor(classId: string, items: Map<string, DescValue>): LayerEffects {
  const out: LayerEffects = {};
  if (classId === 'DrSh' || items.has('DrSh')) {
    const src = classId === 'DrSh' ? items : asObject(items.get('DrSh')) ?? items;
    const base = defaultDropShadow();
    out.dropShadow = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      color: colorFrom(src.get('Clr '), base.color),
      angle: asNumber(src.get('lagl'), base.angle),
      distance: asNumber(src.get('Dstn'), base.distance),
      spread: asNumber(src.get('Ckmt'), base.spread),
      size: asNumber(src.get('blur'), base.size),
    };
  }
  if (classId === 'IrSh' || items.has('IrSh')) {
    const src = classId === 'IrSh' ? items : asObject(items.get('IrSh')) ?? items;
    const base = defaultInnerShadow();
    out.innerShadow = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      color: colorFrom(src.get('Clr '), base.color),
      angle: asNumber(src.get('lagl'), base.angle),
      distance: asNumber(src.get('Dstn'), base.distance),
      choke: asNumber(src.get('Ckmt'), base.choke),
      size: asNumber(src.get('blur'), base.size),
    };
  }
  if (classId === 'OrGl' || items.has('OrGl')) {
    const src = classId === 'OrGl' ? items : asObject(items.get('OrGl')) ?? items;
    const base = defaultOuterGlow();
    out.outerGlow = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      color: colorFrom(src.get('Clr '), base.color),
      spread: asNumber(src.get('Ckmt'), base.spread),
      size: asNumber(src.get('blur'), base.size),
    };
  }
  if (classId === 'IrGl' || items.has('IrGl')) {
    const src = classId === 'IrGl' ? items : asObject(items.get('IrGl')) ?? items;
    const base = defaultInnerGlow();
    out.innerGlow = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      color: colorFrom(src.get('Clr '), base.color),
      choke: asNumber(src.get('Ckmt'), base.choke),
      size: asNumber(src.get('blur'), base.size),
    };
  }
  if (classId === 'FrFX' || items.has('FrFX')) {
    const src = classId === 'FrFX' ? items : asObject(items.get('FrFX')) ?? items;
    const base = defaultStroke();
    const pos = asEnum(src.get('Styl'));
    out.stroke = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      color: colorFrom(src.get('Clr '), base.color),
      size: asNumber(src.get('Sz  '), base.size),
      position: (pos && STROKE_POS[pos]) || base.position,
    };
  }
  if (classId === 'SoFi' || items.has('SoFi')) {
    const src = classId === 'SoFi' ? items : asObject(items.get('SoFi')) ?? items;
    const base = defaultColorOverlay();
    out.colorOverlay = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      color: colorFrom(src.get('Clr '), base.color),
    };
  }
  if (classId === 'GrFl' || items.has('GrFl')) {
    const src = classId === 'GrFl' ? items : asObject(items.get('GrFl')) ?? items;
    const base = defaultGradientOverlay();
    out.gradientOverlay = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      opacity: opacityFrom(src.get('Opct'), base.opacity),
      blendMode: blendFrom(src.get('Md  '), base.blendMode),
      angle: asNumber(src.get('Angl'), base.angle),
      scale: asNumber(src.get('Scl '), base.scale),
      reverse: asBool(src.get('Rvrs'), base.reverse),
    };
  }
  if (classId === 'ebbl' || items.has('ebbl')) {
    const src = classId === 'ebbl' ? items : asObject(items.get('ebbl')) ?? items;
    const base = defaultBevel();
    out.bevel = {
      ...base,
      enabled: asBool(src.get('enab'), true),
      depth: asNumber(src.get('srgR'), base.depth),
      size: asNumber(src.get('blur'), base.size),
      soften: asNumber(src.get('Sftn'), base.soften),
      angle: asNumber(src.get('lagl'), base.angle),
      altitude: asNumber(src.get('Lald'), base.altitude),
      highlightOpacity: opacityFrom(src.get('hglO'), base.highlightOpacity),
      shadowOpacity: opacityFrom(src.get('sdwO'), base.shadowOpacity),
    };
  }
  return out;
}

function mergeEffects(into: LayerEffects, from: LayerEffects): LayerEffects {
  return { ...into, ...from };
}

/**
 * Best-effort Adobe Style Library reader. Returns one `LayerEffects` per style
 * preset found; malformed files yield an empty list rather than throwing.
 */
export function parseAsl(buffer: ArrayBuffer): LayerEffects[] {
  if (buffer.byteLength < 12) return [];
  const r = new Reader(new DataView(buffer));
  const sig = r.ascii(4);
  if (sig !== '8BSL') return [];
  r.u16(); // version
  const count = r.u16();
  const styles: LayerEffects[] = [];
  for (let i = 0; i < count && r.remaining > 16; i += 1) {
    try {
      // Length of the style block that follows (including nested descriptors).
      const blockLen = r.u32();
      const end = r.offset + blockLen;
      r.unicode(); // style name
      const desc = readObject(r);
      let effects: LayerEffects = {};
      if (desc.type === 'object') {
        effects = effectFromDescriptor(desc.classId, desc.items);
        // Lefx holds the effect list in many presets.
        const lefx = asObject(desc.items.get('Lefx'));
        if (lefx) {
          for (const [key, value] of lefx) {
            if (value.type === 'object') {
              effects = mergeEffects(effects, effectFromDescriptor(key, value.items));
            } else if (key.length === 4) {
              effects = mergeEffects(effects, effectFromDescriptor(key, lefx));
            }
          }
        }
        for (const [key, value] of desc.items) {
          if (value.type === 'object' && key !== 'Lefx') {
            effects = mergeEffects(effects, effectFromDescriptor(key, value.items));
          }
        }
      }
      if (Object.keys(effects).length > 0) styles.push(effects);
      if (r.offset < end) r.bytes(end - r.offset);
    } catch {
      break;
    }
  }
  return styles;
}

/** JSON style pack used when a full ASL write is not required. */
export function parseStyleJson(text: string): LayerEffects | null {
  try {
    const raw = JSON.parse(text) as { effects?: LayerEffects } | LayerEffects;
    const effects = 'effects' in raw && raw.effects ? raw.effects : (raw as LayerEffects);
    return Object.keys(effects).length > 0 ? effects : null;
  } catch {
    return null;
  }
}

export function stringifyStyleJson(effects: LayerEffects, name = 'Style'): string {
  return JSON.stringify({ name, effects }, null, 2);
}
